import { useEffect, useMemo } from 'react'

import {
  computeAllocation,
  shouldShowWriteOff,
  computeFxImpact,
  buildPaymentAccountOptions,
  buildPreviewLines,
  computePreviewBalance,
} from './record-payment-helpers'
import { usePaymentFormData } from './usePaymentFormData'

import type { PreviewContext, PreviewLine } from './record-payment-helpers'

// ============================================================================
// Form reset hook
// ============================================================================

export function useFormReset(
  isOpen: boolean,
  outstandingBalance: number,
  setters: {
    setPaymentDate: (d: Date) => void
    setPaymentAmount: (v: number | null) => void
    setPaymentMethod: (v: string) => void
    setPaymentRef: (v: string) => void
    setPaymentNotes: (v: string) => void
    setIntermediaryFee: (v: string) => void
    setSeparateIntermediaryFee: (v: boolean) => void
    setDestinationFee: (v: string) => void
    setSeparateDestinationFee: (v: boolean) => void
    setWriteOffRemaining: (v: boolean) => void
    setTdsAmount: (v: string) => void
  },
): void {
  useEffect(() => {
    if (!isOpen) { return }
    setters.setPaymentDate(new Date())
    setters.setPaymentAmount(outstandingBalance > 0 ? outstandingBalance : null)
    setters.setPaymentMethod('PAYMENT_METHOD_BANK_TRANSFER')
    setters.setPaymentRef('')
    setters.setPaymentNotes('')
    setters.setIntermediaryFee('')
    setters.setSeparateIntermediaryFee(true)
    setters.setDestinationFee('')
    setters.setSeparateDestinationFee(true)
    setters.setWriteOffRemaining(false)
    setters.setTdsAmount('')
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps
}

// ============================================================================
// Fee/TDS account options hooks
// ============================================================================

export function useFeeAccountOptions(
  expenseAccounts: { id: string; code: string; name: string; systemTag?: string }[],
): { value: string; label: string }[] {
  return useMemo(() =>
    [...expenseAccounts]
      .sort((a, b) => {
        const aIsBankFee = a.systemTag === 'bank_fee' ? 0 : 1
        const bIsBankFee = b.systemTag === 'bank_fee' ? 0 : 1
        if (aIsBankFee !== bIsBankFee) { return aIsBankFee - bIsBankFee }
        return a.code.localeCompare(b.code)
      })
      .map(a => ({ value: a.id, label: `${a.code} — ${a.name}` })),
    [expenseAccounts]
  )
}

export function useTdsAccountOptions(
  tdsAccounts: { id: string; code: string; name: string }[],
): { value: string; label: string }[] {
  return useMemo(() => {
    const sorted = [...tdsAccounts].sort((a, b) => {
      const aIsTds = /tds|tax deducted/i.test(a.name || '') || a.code === '1420' || a.code === '2130' ? 0 : 1
      const bIsTds = /tds|tax deducted/i.test(b.name || '') || b.code === '1420' || b.code === '2130' ? 0 : 1
      if (aIsTds !== bIsTds) { return aIsTds - bIsTds }
      return a.code.localeCompare(b.code)
    })
    return sorted.map(a => ({ value: a.id, label: `${a.code} — ${a.name}` }))
  }, [tdsAccounts])
}

// ============================================================================
// Computed payment state hook
// ============================================================================

interface ComputedPaymentState {
  paymentAccountOptions: { value: string; label: string }[]
  feeAccountOptions: { value: string; label: string }[]
  tdsAccountOptions: { value: string; label: string }[]
  allocatedAmount: number
  unallocatedAmount: number
  remainingAfterPayment: number
  isWriteOffVisible: boolean
  fxImpact: number
  previewLines: PreviewLine[]
  previewBalance: { totalDebit: number; totalCredit: number; isBalanced: boolean }
}

interface ComputedPaymentArgs {
  paymentMethod: string
  paymentAmount: number | null
  formData: ReturnType<typeof usePaymentFormData>
  outstandingBalance: number
  isForeignCurrency: boolean
  invoiceCurrency: string
  baseCurrency: string
  invoiceExchangeRate: string
  intermediaryFee: string
  destinationFee: string
  hasSeparateIntermediaryFee: boolean
  selectedBankAccountId: string
  shouldWriteOffRemaining: boolean
  tdsAmount: string
  invoiceType: string
  partyName: string
  isVendorBill: boolean
}

function buildPreviewContext(
  args: ComputedPaymentArgs,
  derived: { allocatedAmount: number; unallocatedAmount: number; remainingAfterPayment: number; fxImpact: number },
): PreviewContext {
  const { formData } = args
  return {
    paymentAmount: args.paymentAmount ?? 0, advanceApplied: formData.advanceApplied ?? 0,
    isForeignCurrency: args.isForeignCurrency, paymentExchangeRate: formData.paymentExchangeRate,
    baseCurrency: args.baseCurrency, invoiceCurrency: args.invoiceCurrency,
    intermediaryFee: args.intermediaryFee, destinationFee: args.destinationFee,
    hasSeparateIntermediaryFee: args.hasSeparateIntermediaryFee,
    selectedBankAccountId: args.selectedBankAccountId,
    bankAccounts: formData.bankAccounts, cashBankGLAccounts: formData.cashBankGLAccounts,
    feeAccountId: formData.feeAccountId, expenseAccounts: formData.expenseAccounts,
    allocatedAmount: derived.allocatedAmount, unallocatedAmount: derived.unallocatedAmount,
    fxImpact: derived.fxImpact,
    receivableAccountCode: formData.receivableAccountCode, receivableAccountName: formData.receivableAccountName,
    advanceAccountCode: formData.advanceAccountCode, advanceAccountName: formData.advanceAccountName,
    shouldWriteOffRemaining: args.shouldWriteOffRemaining, remainingAfterPayment: derived.remainingAfterPayment,
    tdsAmount: args.tdsAmount, tdsAccountId: formData.tdsAccountId, tdsAccounts: formData.tdsAccounts,
    invoiceType: args.invoiceType, partyName: args.partyName, isVendorBill: args.isVendorBill,
  }
}

export function useComputedPaymentState(args: ComputedPaymentArgs): ComputedPaymentState {
  const { formData } = args

  const paymentAccountOptions = useMemo(
    () => buildPaymentAccountOptions(args.paymentMethod, formData.bankAccounts, formData.cashBankGLAccounts),
    [args.paymentMethod, formData.bankAccounts, formData.cashBankGLAccounts]
  )
  const feeAccountOptions = useFeeAccountOptions(formData.expenseAccounts)
  const tdsAccountOptions = useTdsAccountOptions(formData.tdsAccounts)

  const { allocatedAmount, unallocatedAmount, remainingAfterPayment } = useMemo(
    () => computeAllocation(args.paymentAmount ?? 0, formData.advanceApplied ?? 0, args.outstandingBalance),
    [args.paymentAmount, formData.advanceApplied, args.outstandingBalance]
  )
  const isWriteOffVisible = shouldShowWriteOff(remainingAfterPayment)

  const fxImpact = useMemo(
    () => computeFxImpact({ isForeignCurrency: args.isForeignCurrency, paymentExchangeRate: formData.paymentExchangeRate, invoiceExchangeRate: args.invoiceExchangeRate, allocatedAmount, invoiceCurrency: args.invoiceCurrency }),
    [args.isForeignCurrency, formData.paymentExchangeRate, args.invoiceExchangeRate, allocatedAmount, args.invoiceCurrency]
  )

  const previewLines = useMemo(
    () => buildPreviewLines(buildPreviewContext(args, { allocatedAmount, unallocatedAmount, remainingAfterPayment, fxImpact })),
    [args, allocatedAmount, unallocatedAmount, fxImpact, remainingAfterPayment]
  )
  const previewBalance = useMemo(() => computePreviewBalance(previewLines), [previewLines])

  return {
    paymentAccountOptions, feeAccountOptions, tdsAccountOptions,
    allocatedAmount, unallocatedAmount, remainingAfterPayment,
    isWriteOffVisible, fxImpact, previewLines, previewBalance,
  }
}
